import React, { useState, useContext, useEffect, useMemo } from 'react';
import ReactSelect from 'react-select';
import { EnterFirstName, EnterLastName, EmailJobPlaceholder, BirthDateJob, DelimiterPlaceholder, EnterPassword } from '../../../../utils/Constant';
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from 'react-feather';
import { ConfigContext } from "../../../../Api/global/ApiContext";
import { addStudent, validateEmail, validateMobileNumber, getUtilityTimezones, type UtilityTimezone } from '../../../../Api/apiUtils';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Page, PageHeader, Card, Button, Field, TextInput, Select } from '../../../../ui';
import { dsSelectStyles, DS_SELECT_PREFIX } from './dsReactSelect';
import "./addStudent.scss";
import { MixpanelEvents, trackEvent } from '../../../../utils/mixpanelTracking';

interface StudentFormData {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  countryCode?: string;
  mobileNumber?: string;
  gender?: number;
  ageGroup?: string;
  birthDate?: string;
  state?: string;
  timeZone?: string;
  language?: string;
  userType?: string;
}

interface AddStudentProps {
  // fetchStudents: () => void;
}

type Option = { value: string; label: string };

const AddStudent: React.FC<AddStudentProps> = () => {
  const [formData, setFormData] = useState<StudentFormData>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isTestUser, setIsTestUser] = useState(false); // State for checkbox
  const [birthDate, setBirthDate] = useState<Date | null>(null); // State for date picker
  const [emailError, setEmailError] = useState<string>(""); // Track email validation error
  const navigate = useNavigate();
  const { states = [], genders = [], ageGroups = [], countryCodes = [] , languages = []} = useContext(ConfigContext) || {};
  const [timezones, setTimezones] = useState<UtilityTimezone[]>([]);
  const [timezonesLoading, setTimezonesLoading] = useState(true);

  useEffect(() => {
    const loadTimezones = async () => {
      setTimezonesLoading(true);
      try {
        const data = await getUtilityTimezones();
        setTimezones(Array.isArray(data?.timezones) ? data.timezones : []);
      } catch {
        toast.error("Failed to load time zones.");
        setTimezones([]);
      } finally {
        setTimezonesLoading(false);
      }
    };
    void loadTimezones();
  }, []);

  const isIndianTimezone = (timeZone?: string) => {
    const timezoneName = (timeZone || "").toLowerCase();
    return timezoneName === "asia/kolkata" || timezoneName === "asia/calcutta";
  };

  const validateEmailFormat = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Handle email validation without showing toast immediately
    if (name === 'email') {
      if (value && !validateEmailFormat(value)) {
        setEmailError("Please enter a valid email address");
      } else {
        setEmailError(""); // Clear error when email becomes valid
      }
    }

    if (name === "mobileNumber") {
      const maxDigits = String(formData.countryCode || "").trim() === "+91" ? 10 : 15;
      const digitsOnly = value.replace(/\D/g, "").slice(0, maxDigits);
      setFormData({ ...formData, mobileNumber: digitsOnly });
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsTestUser(e.target.checked);
  };

  const handleBirthDateChange = (date: Date | null) => {
    setBirthDate(date);
    setFormData({ ...formData, birthDate: date?.toISOString().split('T')[0] });
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.firstName) {
      toast.error("First name is required");
      return;
    }
    if (!formData.lastName) {
      toast.error("Last name is required");
      return;
    }
    if (!formData.gender) {
      toast.error("Gender is required");
      return;
    }
    if (!formData.language) {
      toast.error("Language is required");
      return;
    }
    if (!formData.email) {
      toast.error("Email is required");
      return;
    }
    if (!validateEmailFormat(formData.email)) {
      if (!emailError) {
        toast.error("Please enter a valid email address (must contain @ and .)");
        setEmailError("Please enter a valid email address (must contain @ and .)");
      }
      return;
    }
    if (!formData.mobileNumber) {
      toast.error("Mobile number is required");
      return;
    }
    if (!formData.timeZone) {
      toast.error("Time zone is required");
      return;
    }
    if (!formData.ageGroup) {
      toast.error("Preferred age group is required");
      return;
    }
    if (isIndianTimezone(formData.timeZone) && !formData.state) {
      toast.error("State is required for Indian numbers");
      return;
    }

    try {
      // First validate email
      try {
        await validateEmail(formData.email);
      } catch (error: any) {
        let errorMessage = "Error validating email";
        if (error.response && typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
        const lowerCaseMessage = errorMessage.toLowerCase().replace(/\s+/g, '');
        if (lowerCaseMessage.includes("emailidalreadyexisted")) {
          toast.error("Email already exists");
        } else {
          toast.error(errorMessage);
        }
        setIsSubmitting(false);
        return;
      }

      // Then validate mobile number
      try {
        await validateMobileNumber(formData.mobileNumber);
      } catch (error: any) {
        let errorMessage = "Error validating mobile number";
        if (error.response && typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
        const lowerCaseMessage = errorMessage.toLowerCase().replace(/\s+/g, '');
        if (lowerCaseMessage.includes("mobilealreadyexisted")) {
          toast.error("Mobile number already exists");
        } else {
          toast.error(errorMessage);
        }
        setIsSubmitting(false);
        return;
      }

      // If both validations pass, proceed with submission
      const payload = {
        firstName: formData.firstName,
        middleName: formData.middleName,
        lastName: formData.lastName,
        emailId: formData.email,
        password: formData.password,
        countryCode: formData.countryCode,
        mobileNumber: formData.mobileNumber,
        genderId: Number(formData.gender),
        ageGroupId: Number(formData.ageGroup),
        birthDate: formData.birthDate 
          ? new Date(formData.birthDate).toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric'
            })
          : undefined,
        state: formData.countryCode === "+91" ? formData.state : undefined,
        preferredTimezone: formData.timeZone,
        preferredlanguage: Number(formData.language),
        userType: isTestUser ? "TEST" : null,
        locale: "en_US",
        interestId: 5,
        isAgreeOnTermsAndConditions: true,
        isReadyForWhatsappUpdates: false
      }

      const response = await addStudent(payload);
      
      // Show success message if no error was thrown
      toast.success("Student added successfully");
      trackEvent(MixpanelEvents.STUDENT_CREATED, {
        isTestUser,
        email: formData.email,
      });
      navigate("/pages/student-profile");
    } catch (error: any) {
      let errorMessage = "Error adding student";
      if (error.response) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
        toast.error(errorMessage);
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error("An unknown error occurred");
      }
      trackEvent(MixpanelEvents.STUDENT_CREATE_FAILED, {
        errorMessage,
        email: formData.email,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const countryCodeOptions = useMemo(
    () =>
      (countryCodes || []).map((code: any): { value: string; label: string } => ({
        value: String(code.countryCode ?? ""),
        label: String(code.countryCode ?? ""),
      })),
    [countryCodes]
  );

  const selectedCountryCodeOption =
    countryCodeOptions.find(
      (opt: { value: string; label: string }) =>
        opt.value === String(formData.countryCode || "")
    ) || null;

  const timezoneOptions = useMemo(
    () =>
      timezones.map((tz): { value: string; label: string } => ({
        value: String(tz.type ?? ""),
        label: String(tz.type ?? ""),
      })),
    [timezones]
  );

  const selectedTimezoneOption =
    timezoneOptions.find(
      (opt: { value: string; label: string }) =>
        opt.value === String(formData.timeZone || "")
    ) || null;

  const indianTimezone = isIndianTimezone(formData.timeZone);
  const mobileMaxLength = String(formData.countryCode || "").trim() === "+91" ? 10 : 15;

  const genderOptions: Option[] = [
    { value: "", label: "Select gender" },
    ...(genders || []).map((gender: any) => ({ value: String(gender.id), label: gender.name })),
  ];
  const ageGroupOptions: Option[] = [
    { value: "", label: "Select age group" },
    ...(ageGroups || []).map((age: any) => ({ value: String(age.id), label: age.name })),
  ];
  const languageOptions: Option[] = [
    { value: "", label: "Select language" },
    ...(languages || []).map((lg: any) => ({ value: String(lg.id), label: lg.name })),
  ];
  const stateOptions: Option[] = [
    { value: "", label: "Select state" },
    ...states.map((state: string) => ({ value: state, label: state })),
  ];

  return (
    <Page className="add-student">
      <PageHeader
        eyebrow="Profiles"
        title="Add Student"
        subtitle="Create a student account. Email and mobile number are checked for duplicates before saving."
        actions={
          <Button variant="ghost" icon={<ArrowLeft size={16} aria-hidden />} onClick={() => navigate(-1)}>
            Back
          </Button>
        }
      />

      <form className="add-student__form" noValidate>
        <Card title="Personal details">
          <div className="add-student__grid">
            <Field label="First name" required>
              {(ctl) => <TextInput {...ctl} type="text" placeholder={EnterFirstName} name="firstName" onChange={handleInputChange} />}
            </Field>
            <Field label="Last name" required>
              {(ctl) => <TextInput {...ctl} type="text" name="lastName" placeholder={EnterLastName} onChange={handleInputChange} />}
            </Field>
            <Field label="Gender" required>
              {(ctl) => <Select {...ctl} name="gender" options={genderOptions} onChange={handleInputChange} />}
            </Field>
            <Field label="Date of birth">
              {(ctl) => (
                <DatePicker
                  id={ctl.id}
                  className="ds-input"
                  wrapperClassName="add-student__datepicker"
                  selected={birthDate}
                  onChange={handleBirthDateChange}
                  dateFormat="dd/MM/yyyy"
                  placeholderText={BirthDateJob}
                />
              )}
            </Field>
            <Field label="Preferred language" required>
              {(ctl) => <Select {...ctl} name="language" options={languageOptions} onChange={handleInputChange} />}
            </Field>
            <Field label="Preferred age group" required>
              {(ctl) => <Select {...ctl} name="ageGroup" options={ageGroupOptions} onChange={handleInputChange} />}
            </Field>
          </div>
        </Card>

        <Card title="Contact and login">
          <div className="add-student__grid">
            <Field label="Email" required error={emailError || undefined}>
              {(ctl) => (
                <TextInput
                  {...ctl}
                  placeholder={EmailJobPlaceholder}
                  type="email"
                  name="email"
                  onChange={handleInputChange}
                  value={formData.email || ""}
                />
              )}
            </Field>
            <Field label="Password">
              {(ctl) => <TextInput {...ctl} type="text" name="password" placeholder={EnterPassword} onChange={handleInputChange} />}
            </Field>
            <Field label="Mobile number" required>
              {(ctl) => (
                <div className="add-student__phone">
                  <ReactSelect<Option>
                    aria-label="Country code"
                    options={countryCodeOptions}
                    value={selectedCountryCodeOption}
                    onChange={(option) => {
                      const nextCode = option?.value || "";
                      const maxDigits = String(nextCode).trim() === "+91" ? 10 : 15;
                      const nextMobile = String(formData.mobileNumber || "")
                        .replace(/\D/g, "")
                        .slice(0, maxDigits);
                      setFormData({
                        ...formData,
                        countryCode: nextCode,
                        mobileNumber: nextMobile,
                      });
                    }}
                    isSearchable
                    isClearable
                    placeholder="Code"
                    noOptionsMessage={() => "No match"}
                    filterOption={(option, rawInput) => {
                      const q = rawInput.trim().toLowerCase().replace(/\s+/g, "");
                      if (!q) return true;
                      const label = String(option.label)
                        .toLowerCase()
                        .replace(/\s+/g, "");
                      return (
                        label.includes(q) ||
                        label.replace(/^\+/, "").includes(q.replace(/^\+/, ""))
                      );
                    }}
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                    menuPosition="fixed"
                    menuPlacement="auto"
                    classNamePrefix={DS_SELECT_PREFIX}
                    styles={dsSelectStyles<Option>()}
                  />
                  <TextInput
                    {...ctl}
                    type="tel"
                    placeholder={DelimiterPlaceholder}
                    name="mobileNumber"
                    value={formData.mobileNumber ?? ""}
                    onChange={handleInputChange}
                    maxLength={mobileMaxLength}
                    inputMode="numeric"
                  />
                </div>
              )}
            </Field>
            <Field label="Time zone" required>
              {(ctl) => (
                <ReactSelect<Option>
                  inputId={ctl.id}
                  options={timezoneOptions}
                  value={selectedTimezoneOption}
                  onChange={(option) => {
                    const nextTz = option?.value || "";
                    setFormData({
                      ...formData,
                      timeZone: nextTz,
                      // Clear state when leaving Indian timezone
                      ...(isIndianTimezone(nextTz) ? {} : { state: "" }),
                    });
                  }}
                  isDisabled={timezonesLoading}
                  isSearchable
                  isClearable
                  placeholder={
                    timezonesLoading
                      ? "Loading time zones..."
                      : "Search or select time zone"
                  }
                  noOptionsMessage={() => "No matching time zone"}
                  filterOption={(option, rawInput) => {
                    const q = rawInput.trim().toLowerCase();
                    if (!q) return true;
                    const label = String(option.label).toLowerCase();
                    const city = label.includes("/")
                      ? label.split("/").pop() || ""
                      : label;
                    return (
                      label.includes(q) ||
                      city.includes(q) ||
                      label.replace(/[_/]/g, " ").includes(q)
                    );
                  }}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPosition="fixed"
                  menuPlacement="auto"
                  classNamePrefix={DS_SELECT_PREFIX}
                  styles={dsSelectStyles<Option>()}
                />
              )}
            </Field>
            <Field
              label="State"
              required={indianTimezone}
              hint={
                states.length === 0
                  ? "Loading states..."
                  : formData.timeZone && !indianTimezone
                    ? "Only needed for the India time zone."
                    : undefined
              }
            >
              {(ctl) => (
                <Select
                  {...ctl}
                  name="state"
                  options={stateOptions}
                  onChange={handleInputChange}
                  disabled={Boolean(formData.timeZone) && !indianTimezone}
                  value={indianTimezone ? formData.state : ""}
                />
              )}
            </Field>
          </div>
        </Card>

        <Card title="Account type">
          <div className="add-student__check">
            <input
              id="add-student-test-user"
              type="checkbox"
              className="form-check-input"
              checked={isTestUser}
              onChange={handleCheckboxChange}
            />
            <label className="form-check-label" htmlFor="add-student-test-user">Test user</label>
          </div>
        </Card>

        <div className="add-student__actions">
          <Button variant="primary" onClick={handleSubmit} loading={isSubmitting}>Add student</Button>
        </div>
      </form>
    </Page>
  );
};

export default AddStudent;
